/* eslint-disable simple-import-sort/imports */
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { toNumber } from 'lodash';
import { Loader2 } from 'lucide-react';
import React, { useMemo } from 'react';

import { Alert, AlertDescription, AlertTitle } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Label } from 'shadcn/components/ui/Label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { Tooltip, TooltipContent, TooltipTrigger } from 'shadcn/components/ui/tooltip';

import { PurchaseOrderItemLookup } from 'components/procurement/components/purchaseOrdersV2/formComponents/PurchaseOrderItemLookup';
import { ReasonCodePicker } from 'components/procurement/components/purchaseOrdersV2/formComponents/ReasonCodePicker';
import { usePurchaseOrderItems } from 'components/procurement/components/purchaseOrdersV2/hooks/usePurchaseOrderItems';
import { useCountriesQuery } from 'components/procurement/components/purchaseOrdersV2/queries/AddressesQueries';
import { usePurchaseOrderQuery } from 'components/procurement/components/purchaseOrdersV2/queries/PurchaseOrderQueries';
import { formatPurchaseOrderDate } from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDateUtils';
import {
    FALLBACK_TEXT,
    formatCurrency,
    formatNumber,
    normalizeOptionalText,
} from 'components/procurement/components/purchaseOrdersV2/utils/purchaseOrderDisplayUtils';

import { PurchaseOrderItemEditTable } from './PurchaseOrderItemEditTable';

const EMPTY_SET = new Set();
const noop = () => {};

function NameCell({ getValue }) {
    const value = normalizeOptionalText(getValue());
    if (value === FALLBACK_TEXT) return <span className="text-left">{value}</span>;

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span className="block max-w-[300px] truncate text-left">{value}</span>
            </TooltipTrigger>
            <TooltipContent side="top">{value}</TooltipContent>
        </Tooltip>
    );
}

function WhereWouldItGoCell({ getValue }) {
    const value = normalizeOptionalText(getValue());
    if (value === FALLBACK_TEXT) return <span className="text-left">{value}</span>;

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span className="block max-w-[240px] truncate text-left">{value}</span>
            </TooltipTrigger>
            <TooltipContent side="top">{value}</TooltipContent>
        </Tooltip>
    );
}

function WhyImMisfitCell({ getValue }) {
    const value = normalizeOptionalText(getValue());
    if (value === FALLBACK_TEXT) return <span className="text-left">{value}</span>;

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span className="block max-w-[240px] truncate text-left">{value}</span>
            </TooltipTrigger>
            <TooltipContent side="top">{value}</TooltipContent>
        </Tooltip>
    );
}

/**
 * Renders the purchase order items card shell.
 * @param {JSX.Element} children - The children to render inside the card shell.
 * @returns {JSX.Element} Purchase order items card shell.
 */
function PurchaseOrderItemsCardShell({ children }) {
    return (
        <div className="rounded-md border bg-card p-6 shadow-sm">
            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                    <h2 className="text-left text-lg font-semibold tracking-tight text-foreground">
                        Purchase Order Items
                    </h2>
                </div>
                {children}
            </div>
        </div>
    );
}

/**
 * Displays the purchase order items section in either read-only or inline-edit mode.
 *
 * Data can be provided by the parent (`purchaseOrder`, `countries`). If not provided, this component
 * will fetch missing data using `usePurchaseOrderQuery` and/or `useCountriesQuery`.
 *
 * In edit mode, renders `PurchaseOrderItemEditTable` and shows reason-code selectors when needed.
 *
 * @param {Object} props
 * @param {string|number} [props.purchaseOrderId] - Purchase order ID used for fetching when `purchaseOrder` is not provided.
 * @param {Object|null} [props.purchaseOrder] - Purchase order detail object (must include `items` or `purchaseOrderItems`).
 * @param {Array<{id:number|string,name?:string|null,code?:string|null}>} [props.countries] - Countries list for resolving country-of-origin display/options.
 * @param {boolean} [props.isEditing=false] - When true, shows inline item edit UI.
 * @param {{current: {inputs: Object, selects: Object, multi: Object}}} [props.itemEditStateRef] - Ref storing uncontrolled input/select values keyed by row.
 * @param {Set<string|number>} [props.removedItemIds] - Set of PO map IDs marked for removal.
 * @param {(updater:Set<string|number>|((prev:Set<string|number>)=>Set<string|number>))=>void} [props.setRemovedItemIds] - Setter for `removedItemIds`.
 * @param {() => void} [props.onItemEditsChange] - Callback invoked when any item edit changes (used to recompute derived edit state).
 * @param {string} [props.qtyReasonCode] - Selected reason code for quantity edits.
 * @param {(value:string)=>void} [props.setQtyReasonCode] - Setter for `qtyReasonCode`.
 * @param {string} [props.removeReasonCode] - Selected reason code for item removals.
 * @param {(value:string)=>void} [props.setRemoveReasonCode] - Setter for `removeReasonCode`.
 * @param {boolean} [props.hasRemovals=false] - Whether any items are marked for removal (controls remove reason UI).
 * @param {boolean} [props.hasQtyEdits=false] - Whether any item quantity changes exist (controls qty reason UI).
 * @param {Array<{id: string, sku?: string, messages: string[]}>} [props.itemErrors]
 * @param {string[]} [props.itemSummaryErrors]
 * @param {boolean} [props.showValidation]
 * @param {(rowId:string|number)=>void} [props.onRemoveNewRow]
 * @returns {JSX.Element} Purchase order items card.
 */
export function PurchaseOrderItemInfoCard({
    addedItems = [],
    addItemReasonCode = '',
    canAddItem = false,
    canClearSelection = false,
    countries = [],
    hasAdditions = false,
    hasQtyEdits = false,
    hasRemovals = false,
    isEditing = false,
    itemEditStateRef,
    itemErrors = [],
    itemSummaryErrors = [],
    onAddItem = noop,
    onClearSelection = noop,
    onExternalSkuChange = noop,
    onItemEditsChange = noop,
    onItemMasterSkuChange = noop,
    onRemoveNewRow = noop,
    purchaseOrder = null,
    purchaseOrderId,
    qtyReasonCode = '',
    removedItemIds = EMPTY_SET,
    removeReasonCode = '',
    selectedExternalSku = '',
    selectedItemMasterSku = '',
    setAddItemReasonCode = noop,
    setQtyReasonCode = noop,
    setRemovedItemIds = noop,
    setRemoveReasonCode = noop,
    showValidation = false,
}) {
    const shouldFetchPurchaseOrder = !purchaseOrder && Boolean(purchaseOrderId);
    const {
        data: fetchedPurchaseOrder,
        isError: isPurchaseOrderError,
        isFetching: isFetchingPurchaseOrder,
        isLoading: isLoadingPurchaseOrder,
    } = usePurchaseOrderQuery({ id: purchaseOrderId, enabled: shouldFetchPurchaseOrder });

    const shouldFetchCountries = !Array.isArray(countries) || countries.length === 0;
    const {
        data: fetchedCountries,
        isError: isCountriesError,
        isFetching: isFetchingCountries,
        isLoading: isLoadingCountries,
    } = useCountriesQuery({ enabled: shouldFetchCountries });

    const resolvedPurchaseOrder = purchaseOrder ?? fetchedPurchaseOrder ?? null;
    const resolvedCountries = Array.isArray(countries) && countries.length > 0 ? countries : (fetchedCountries ?? []);

    const countryLookup = useMemo(() => {
        if (!Array.isArray(resolvedCountries) || resolvedCountries.length === 0) return null;

        const map = new Map(
            resolvedCountries
                .filter((country) => country?.id !== null && country?.id !== undefined)
                .map((country) => [String(country.id), country]),
        );

        return (id) => map.get(id) ?? null;
    }, [resolvedCountries]);

    const fetchedItems = resolvedPurchaseOrder?.items ?? resolvedPurchaseOrder?.purchaseOrderItems ?? null;

    const tableData = usePurchaseOrderItems(fetchedItems, countryLookup);

    // Merge fetched items with newly added items for edit mode display
    const combinedTableData = useMemo(() => [...tableData, ...(addedItems ?? [])], [addedItems, tableData]);
    const showErrors = showValidation === true;
    const immediateReasonCodeMessages = useMemo(() => {
        const messages = [];
        if (hasAdditions && !addItemReasonCode) {
            messages.push('Reason code is required when adding items');
        }
        if (hasRemovals && !removeReasonCode) {
            messages.push('Remove reason code is required when removing items');
        }
        if (hasQtyEdits && !qtyReasonCode) {
            messages.push('Qty reason code is required when changing case quantity');
        }
        return messages;
    }, [addItemReasonCode, hasAdditions, hasQtyEdits, hasRemovals, qtyReasonCode, removeReasonCode]);
    const hasImmediateReasonCodeErrors = immediateReasonCodeMessages.length > 0;
    const itemErrorMessages = useMemo(() => {
        const messages = [];
        if (showErrors) {
            messages.push(...itemSummaryErrors.map((message) => ({ key: `summary-${message}`, message })));
            messages.push(
                ...itemErrors.flatMap((error) =>
                    error.messages.map((message) => ({
                        key: `item-${error.id}-${message}`,
                        message,
                    })),
                ),
            );
        }

        const reasonMessagesToAdd = showErrors
            ? immediateReasonCodeMessages.filter((message) => !itemSummaryErrors.includes(message))
            : immediateReasonCodeMessages;

        messages.push(...reasonMessagesToAdd.map((message) => ({ key: `reason-${message}`, message })));

        return messages;
    }, [immediateReasonCodeMessages, itemErrors, itemSummaryErrors, showErrors]);

    const columnDefs = useMemo(() => {
        const columns = [
            {
                id: 'itemMasterSku',
                header: 'Item Master SKU',
                accessorFn: (row) => row?.sku ?? row?.itemMasterSku ?? row?.itemMasterID ?? null,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'externalSku',
                header: 'External SKU',
                accessorFn: (row) => row?.externalSku ?? null,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'name',
                header: 'Name',
                accessorFn: (row) => row?.itemName ?? row?.name ?? null,
                cell: NameCell,
            },
            {
                id: 'expectedExpiration',
                header: 'Expected Expiration',
                accessorFn: (row) => row?.expectedExpirationDate ?? row?.expiresOn ?? null,
                cell: ({ getValue }) => formatPurchaseOrderDate(getValue(), FALLBACK_TEXT),
            },
            {
                id: 'expectedProduction',
                header: 'Expected Production',
                accessorFn: (row) => row?.expectedProductionDate ?? null,
                cell: ({ getValue }) => formatPurchaseOrderDate(getValue(), FALLBACK_TEXT),
            },
            {
                id: 'caseQuantity',
                header: 'Case Quantity',
                accessorFn: (row) => row?.qty ?? row?.caseQuantity ?? row?.caseQty ?? null,
                cell: ({ getValue }) => formatNumber(getValue()),
            },
            {
                id: 'packSize',
                header: 'Pack Size',
                accessorFn: (row) => row?.purchasingCasePackSize ?? row?.packSize ?? null,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'vendorSku',
                header: 'Vendor SKU',
                accessorFn: (row) => row?.vendorSku ?? null,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'casesPerPallet',
                header: 'Cases per Pallet',
                accessorFn: (row) => row?.csPallet ?? null,
                cell: ({ getValue }) => formatNumber(getValue()),
            },
            {
                id: 'totalUnits',
                header: 'Total Units',
                accessorFn: (row) => toNumber(row.qty) * toNumber(row.baseUnitsPerCase),
                cell: ({ getValue }) => formatNumber(getValue()),
            },
            {
                id: 'casePrice',
                header: 'Case Price',
                accessorFn: (row) => row?.unitPrice ?? row?.casePrice ?? null,
                cell: ({ getValue }) => formatCurrency(getValue()),
            },
            {
                id: 'totalCost',
                header: 'Total Cost',
                accessorFn: (row) => toNumber(row.qty) * row.unitPrice,
                cell: ({ getValue }) => formatCurrency(getValue()),
            },
            {
                id: 'week',
                header: 'Week',
                accessorFn: (row) => row?.week ?? row?.shipWeek ?? null,
                cell: ({ getValue }) => formatNumber(getValue()),
            },
            {
                id: 'year',
                header: 'Year',
                accessorFn: (row) => row?.year ?? row?.shipYear ?? null,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'purchaseType',
                header: 'Purchase Type',
                accessorFn: (row) => row?.purchaseType ?? null,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            {
                id: 'srp',
                header: 'SRP',
                accessorFn: (row) => row?.suggestedRetailPrice ?? row?.msrpPrice ?? null,
                cell: ({ getValue }) => formatCurrency(getValue()),
            },
            {
                id: 'whereWouldItGo',
                header: 'Where Would it Go?',
                accessorFn: (row) => row?.unpurchasedOutcome ?? row?.whereWouldItGo ?? null,
                cell: WhereWouldItGoCell,
            },
            {
                id: 'whyImMisfit',
                header: "Why I'm a Misfit",
                accessorFn: (row) => {
                    if (Array.isArray(row?.sourcingReasons) && row.sourcingReasons.length > 0) {
                        return row.sourcingReasons.filter(Boolean).join(', ');
                    }
                    return row?.whyImAMisfit ?? row?.whyImMisfit ?? row?.misfitReason ?? null;
                },
                cell: WhyImMisfitCell,
            },
            {
                id: 'countryOfOrigin',
                header: 'Country of Origin',
                accessorFn: (row) =>
                    row?.countryOfOriginName ??
                    row?.countryOfOrigin ??
                    row?.countryOfOriginId ??
                    row?.countryOfOriginID ??
                    null,
                cell: ({ getValue }) => normalizeOptionalText(getValue()),
            },
            // Inline edit mode uses a separate table component
        ];

        return columns;
    }, []);

    const table = useReactTable({
        data: tableData,
        columns: columnDefs,
        getCoreRowModel: getCoreRowModel(),
    });

    // Use combined data for item count in edit mode, fetched data in read mode
    const itemCount = isEditing ? combinedTableData.length : tableData.length;
    const isCountriesLoadingState = (isLoadingCountries || isFetchingCountries) && !countryLookup;
    const isLoadingItems = isLoadingPurchaseOrder || isFetchingPurchaseOrder || isCountriesLoadingState;
    const isErrorState = isPurchaseOrderError || isCountriesError;

    if (isLoadingItems) {
        return (
            <PurchaseOrderItemsCardShell>
                <div className="flex min-h-[160px] items-center justify-center rounded-md border border-border px-4 py-10 text-center text-sm text-muted-foreground">
                    <div className="flex flex-col items-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        <span>Loading purchase order items…</span>
                    </div>
                </div>
            </PurchaseOrderItemsCardShell>
        );
    }

    if (isErrorState) {
        return (
            <PurchaseOrderItemsCardShell>
                <div className="flex min-h-[160px] items-center justify-center rounded-md border border-border px-4 py-10 text-center text-sm text-muted-foreground">
                    Unable to load purchase order items.
                </div>
            </PurchaseOrderItemsCardShell>
        );
    }

    if (isEditing) {
        return (
            <PurchaseOrderItemsCardShell>
                <div className="flex flex-col gap-4">
                    {(showErrors || hasImmediateReasonCodeErrors) && itemErrorMessages.length > 0 ? (
                        <Alert variant="destructive">
                            <AlertTitle className="text-left">Fix the following item issues to save</AlertTitle>
                            <AlertDescription className="text-left">
                                <ul className="list-disc pl-5 text-left">
                                    {itemErrorMessages.map(({ key, message }) => (
                                        <li key={key}>{message}</li>
                                    ))}
                                </ul>
                            </AlertDescription>
                        </Alert>
                    ) : null}
                    <div className="flex flex-col gap-3 border-b border-border pb-4">
                        <Label className="text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                            Add Item to Purchase Order
                        </Label>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            <div className="md:col-span-2">
                                <PurchaseOrderItemLookup
                                    externalSku={selectedExternalSku}
                                    itemMasterSku={selectedItemMasterSku}
                                    onExternalSkuChange={onExternalSkuChange}
                                    onItemMasterSkuChange={onItemMasterSkuChange}
                                />
                            </div>
                            <div className="flex flex-col justify-end">
                                <div className="flex gap-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={onClearSelection}
                                        disabled={!canClearSelection}
                                    >
                                        Clear
                                    </Button>
                                    <Button type="button" onClick={onAddItem} disabled={!canAddItem}>
                                        Add Item
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {combinedTableData.length > 0 ? (
                        <PurchaseOrderItemEditTable
                            rows={combinedTableData}
                            countries={resolvedCountries}
                            itemEditStateRef={itemEditStateRef}
                            removedItemIds={removedItemIds}
                            setRemovedItemIds={setRemovedItemIds}
                            onItemEditsChange={onItemEditsChange}
                            onRemoveNewRow={onRemoveNewRow}
                        />
                    ) : (
                        <div className="flex min-h-[120px] items-center justify-center rounded-md border border-border px-4 py-10 text-center text-sm text-muted-foreground">
                            No items yet. Add an item using the lookup above.
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        {hasQtyEdits && (
                            <div className="flex flex-col gap-2">
                                <ReasonCodePicker
                                    label="Qty Reason Code"
                                    value={qtyReasonCode}
                                    onValueChange={setQtyReasonCode}
                                />
                            </div>
                        )}

                        {hasRemovals && (
                            <div className="flex flex-col gap-2">
                                <ReasonCodePicker
                                    label={
                                        <>
                                            Remove Reason Code <span className="text-destructive">*</span>
                                        </>
                                    }
                                    value={removeReasonCode}
                                    onValueChange={setRemoveReasonCode}
                                />
                            </div>
                        )}

                        {hasAdditions && (
                            <div className="flex flex-col gap-2">
                                <ReasonCodePicker
                                    label={
                                        <>
                                            Add Item Reason Code <span className="text-destructive">*</span>
                                        </>
                                    }
                                    value={addItemReasonCode}
                                    onValueChange={setAddItemReasonCode}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </PurchaseOrderItemsCardShell>
        );
    }

    if (itemCount) {
        return (
            <PurchaseOrderItemsCardShell>
                <Table
                    className="rounded-md border border-border"
                    style={{
                        minWidth: '1500px',
                    }}
                >
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead
                                        key={header.id}
                                        className="whitespace-nowrap text-left text-xs font-semibold uppercase tracking-wide"
                                    >
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell
                                        key={cell.id}
                                        className="whitespace-pre-wrap break-words align-top text-sm"
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </PurchaseOrderItemsCardShell>
        );
    }

    return (
        <PurchaseOrderItemsCardShell>
            <div className="flex min-h-[160px] items-center justify-center rounded-md border border-border px-4 py-10 text-center text-sm text-muted-foreground">
                No items associated with this purchase order.
            </div>
        </PurchaseOrderItemsCardShell>
    );
}

PurchaseOrderItemInfoCard.displayName = 'PurchaseOrderItemInfoCard';
